import { useNavigate } from "react-router";
import { twJoin } from "tailwind-merge";

import type { DocumentsRootFolderDto } from "@/api/types";
import { ContextMenu, type ContextMenuItem } from "@/components/ContextMenu/ContextMenu";
import { Icon54 } from "@/components/Icon54/Icon54";
import { Edit05Icon } from "@/components/Icons/Edit05Icon";
import { Trash02Icon } from "@/components/Icons/Trash02Icon";
import { Info } from "@/components/Info/Info";
import { usePermission } from "@/hooks/usePermission";
import { useSlug } from "@/hooks/useSlug";
import { canManageAllDocuments } from "@/modules/documents/permissions";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

interface RootFolderProps {
  folder: DocumentsRootFolderDto;
  onDelete: (id: string) => void;
}

export function RootFolder({ folder, onDelete }: RootFolderProps): React.ReactNode {
  const slug = useSlug();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const hasPermission = usePermission();

  const menuActions: ContextMenuItem[] = [
    {
      labels: {
        default: t("common.action.edit"),
      },
      icon: <Edit05Icon />,
      "data-testid": "context-menu-edit-btn",
      value: "edit",
      callbackFn: () => {
        void navigate(routes.documents.rootEdit({ slug, folderId: folder.id }));
      },
      isHidden: !hasPermission(canManageAllDocuments),
    },
    {
      labels: {
        default: t("common.action.delete"),
        disabled: t("model.document.folder.action.delete.not-allowed"),
      },
      icon: <Trash02Icon />,
      "data-testid": "context-menu-delete-btn",
      value: "delete",
      callbackFn: () => onDelete(folder.id),
      isHidden: !hasPermission(canManageAllDocuments),
      isDisabled: !folder.isEmpty,
    },
  ];

  const handleNavigateFolder = () => {
    if (folder.isEmpty && !folder.canAddItem) {
      return;
    }

    void navigate(routes.documents.documentsList({ slug, folderId: folder.id }));
  };

  return (
    // Mocked "<a/>" used to allow interactive elements inside without propagating the click event
    <div
      className={twJoin(
        "flex h-40 flex-col items-center justify-center gap-4 rounded-lg bg-white px-4 shadow-sm",
        folder.isEmpty && !folder.canAddItem ? "pointer-events-none opacity-50" : "cursor-pointer hover:shadow-md",
      )}
      role="link"
      tabIndex={0}
      aria-label={`Open "${folder.name}" folder`}
      onClick={handleNavigateFolder}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          handleNavigateFolder();
        }
      }}
      data-testid="documents-root-item"
    >
      <div className="relative flex w-full items-start gap-2 md:gap-4">
        <div className="flex rounded-lg p-4 ring-1 ring-grey-100">
          <Icon54 className="size-12 text-[3rem]" char={folder.iconChar} />
        </div>

        <div className="flex flex-1 items-center justify-between gap-4">
          <div className="flex h-fit flex-col gap-1">
            <h2 className="line-clamp-1 text-body-bold">{folder.name}</h2>
            {folder.description && <p className="line-clamp-2 text-caption text-grey-700">{folder.description}</p>}
          </div>
          <ContextMenu items={menuActions} />
        </div>
        <div className="absolute -bottom-2 left-0 flex w-full translate-y-full items-center justify-between pl-24">
          {folder.isEmpty && folder.canAddItem && (
            <div className="flex items-center gap-2" data-testid="documents-root-item-empty">
              <span className="text-overline text-grey-700">{t("page.document.root.list.disabled")}</span>
              <Info tooltip={t("page.document.root.list.disabled.description")} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
